import {get, ref} from 'firebase/database';
import {database} from '../config/firebase';
import * as XLSX from 'xlsx';
import {jsPDF} from "jspdf";
import 'jspdf-autotable';

// Exporter la liste des membres au format Excel
export const exportToExcel = async (churchId = null) => {
    try {
        const members = await fetchMembers(churchId);

        if (members.length === 0) {
            throw new Error("Aucune donnée à exporter");
        }

        const worksheet = XLSX.utils.json_to_sheet(members);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Membres");

        // Formatter les colonnes
        const columnWidths = [
            {wpx: 150}, // Nom
            {wpx: 150}, // Prénom
            {wpx: 120}, // Téléphone
            {wpx: 200}, // Email
            {wpx: 80},  // Sexe
            {wpx: 100}, // Baptisé
            {wpx: 150}  // Profession
        ];
        worksheet['!cols'] = columnWidths;

        // Générer le fichier Excel
        const excelBuffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
        const blob = new Blob([excelBuffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});

        // Créer un lien de téléchargement
        const fileName = churchId ? `membres_eglise_${churchId}.xlsx` : 'tous_les_membres.xlsx';
        downloadFile(blob, fileName);

        return {success: true};
    } catch (error) {
        console.error("Erreur lors de l'exportation vers Excel:", error);
        return {success: false, error: error.message};
    }
};

// Exporter la liste des membres au format PDF
export const exportToPDF = async (churchId = null) => {
    try {
        const members = await fetchMembers(churchId);

        if (members.length === 0) {
            throw new Error("Aucune donnée à exporter");
        }

        // Récupérer des informations supplémentaires pour le titre du PDF
        let title = "Liste des membres";
        if (churchId) {
            const churchRef = ref(database, `churches/${churchId}`);
            const churchSnapshot = await get(churchRef);
            if (churchSnapshot.exists()) {
                title = `Liste des membres - ${churchSnapshot.val().nom}`;
            }
        }

        // Créer un nouveau document PDF
        const doc = new jsPDF();

        // Ajouter le titre
        doc.setFontSize(16);
        doc.text(title, 14, 22);

        // Ajouter la date
        doc.setFontSize(10);
        doc.text(`Généré le ${new Date().toLocaleDateString()}`, 14, 30);

        // Préparer les données pour le tableau
        const tableData = members.map(member => [
            member.nom,
            member.prenom,
            member.telephone,
            member.sexe,
            member.baptise ? "Oui" : "Non",
            member.profession
        ]);

        // Générer le tableau
        doc.autoTable({
            startY: 35,
            head: [['Nom', 'Prénom', 'Téléphone', 'Sexe', 'Baptisé', 'Profession']],
            body: tableData,
            theme: 'grid',
            styles: {fontSize: 8, cellPadding: 3},
            columnStyles: {
                0: {cellWidth: 30},
                1: {cellWidth: 30},
                2: {cellWidth: 30},
                3: {cellWidth: 15},
                4: {cellWidth: 20},
                5: {cellWidth: 40}
            }
        });

        // Générer le fichier PDF
        const pdfBlob = doc.output('blob');

        // Créer un lien de téléchargement
        const fileName = churchId ? `membres_eglise_${churchId}.pdf` : 'tous_les_membres.pdf';
        downloadFile(pdfBlob, fileName);

        return {success: true};
    } catch (error) {
        console.error("Erreur lors de l'exportation vers PDF:", error);
        return {success: false, error: error.message};
    }
};

// Exporter les statistiques au format PDF
export const exportStatsToPDF = async (statistics, churchId = null) => {
    try {
        if (!statistics) {
            throw new Error("Aucune statistique à exporter");
        }

        // Déterminer le titre
        let title = "Statistiques des membres";
        if (churchId) {
            const churchRef = ref(database, `churches/${churchId}`);
            const churchSnapshot = await get(churchRef);
            if (churchSnapshot.exists()) {
                title = `Statistiques - ${churchSnapshot.val().nom}`;
            }
        }

        // Créer un nouveau document PDF
        const doc = new jsPDF();

        // Ajouter le titre
        doc.setFontSize(16);
        doc.text(title, 14, 22);

        // Ajouter la date
        doc.setFontSize(10);
        doc.text(`Généré le ${new Date().toLocaleDateString()}`, 14, 30);

        // Sélectionner les données à utiliser (global ou église)
        const data = churchId && statistics.church ? statistics.church : statistics.global;

        if (data) {
            // Données principales
            const mainData = [
                ['Total des membres', data.totalMembres || 0],
                ['Hommes', data.hommes || 0],
                ['Femmes', data.femmes || 0],
                ['Baptisés', data.baptises || 0],
                ['Non-baptisés', (data.totalMembres || 0) - (data.baptises || 0)]
            ];

            // Générer le tableau principal
            doc.autoTable({
                startY: 40,
                head: [['Catégorie', 'Nombre']],
                body: mainData,
                theme: 'grid'
            });
        }

        // Ajouter les statistiques par profession si disponibles
        if (statistics.professions) {
            const professionsData = Object.entries(statistics.professions)
                .map(([name, count]) => [name, count])
                .sort((a, b) => b[1] - a[1]);

            doc.setFontSize(14);
            doc.text("Répartition par profession", 14, 100);

            if (professionsData.length > 0) {
                doc.autoTable({
                    startY: 105,
                    head: [['Profession', 'Nombre']],
                    body: professionsData,
                    theme: 'grid'
                });
            } else {
                doc.setFontSize(10);
                doc.text("Aucune donnée disponible", 14, 110);
            }
        }

        // Générer le fichier PDF
        const pdfBlob = doc.output('blob');

        // Créer un lien de téléchargement
        const fileName = churchId ? `statistiques_eglise_${churchId}.pdf` : 'statistiques_globales.pdf';
        downloadFile(pdfBlob, fileName);

        return {success: true};
    } catch (error) {
        console.error("Erreur lors de l'exportation des statistiques:", error);
        return {success: false, error: error.message};
    }
};

// NOUVELLE FONCTION : Exporter le récapitulatif des églises en PDF
export const exportChurchesSummaryToPDF = async (churchesData, globalSummary) => {
    try {
        if (!churchesData || churchesData.length === 0) {
            throw new Error("Aucune donnée d'église à exporter");
        }

        // Créer un nouveau document PDF en format paysage pour plus d'espace
        const doc = new jsPDF('landscape');

        // En-tête du document
        doc.setFontSize(18);
        doc.setFont('helvetica', 'bold');
        doc.text('RÉCAPITULATIF DES MEMBRES PAR ÉGLISES', 14, 20);

        doc.setFontSize(12);
        doc.setFont('helvetica', 'normal');
        doc.text('Registre JEE/SIM', 14, 28);

        doc.setFontSize(10);
        doc.text(`Rapport généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 14, 35);

        // Section du résumé global
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('RÉSUMÉ GLOBAL', 14, 50);

        // Tableau du résumé global
        const globalSummaryData = [
            ['Nombre total d\'églises', globalSummary.totalChurches],
            ['Total des membres', globalSummary.totalMembers],
            ['Hommes', globalSummary.totalMen],
            ['Femmes', globalSummary.totalWomen],
            ['Baptisés', globalSummary.totalBaptized],
            ['Non-baptisés', globalSummary.totalNonBaptized]
        ];

        doc.autoTable({
            startY: 55,
            head: [['Indicateur', 'Valeur']],
            body: globalSummaryData,
            theme: 'grid',
            styles: {
                fontSize: 10,
                cellPadding: 4,
                halign: 'left'
            },
            columnStyles: {
                0: { cellWidth: 80, fontStyle: 'bold' },
                1: { cellWidth: 40, halign: 'center', fontStyle: 'bold' }
            },
            headStyles: {
                fillColor: [59, 130, 246], // Bleu indigo
                textColor: 255,
                fontStyle: 'bold'
            }
        });

        // Calculer la position Y après le premier tableau
        let currentY = doc.lastAutoTable.finalY + 15;

        // Section détaillée par église
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('DÉTAIL PAR ÉGLISE', 14, currentY);

        // Préparer les données pour le tableau détaillé
        const tableData = churchesData.map(church => [
            church.nom,
            church.localisation || '-',
            church.totalMembres,
            church.hommes,
            church.femmes,
            church.baptises,
            church.nonBaptises,
            church.contact || '-'
        ]);

        // Tableau détaillé des églises
        doc.autoTable({
            startY: currentY + 5,
            head: [['Église', 'Localisation', 'Total', 'H', 'F', 'Bapt.', 'N.Bapt.', 'Contact']],
            body: tableData,
            theme: 'grid',
            styles: {
                fontSize: 8,
                cellPadding: 2
            },
            columnStyles: {
                0: { cellWidth: 60 }, // Église
                1: { cellWidth: 50 }, // Localisation
                2: { cellWidth: 20, halign: 'center' }, // Total
                3: { cellWidth: 15, halign: 'center' }, // Hommes
                4: { cellWidth: 15, halign: 'center' }, // Femmes
                5: { cellWidth: 20, halign: 'center' }, // Baptisés
                6: { cellWidth: 20, halign: 'center' }, // Non-baptisés
                7: { cellWidth: 50 } // Contact
            },
            headStyles: {
                fillColor: [59, 130, 246],
                textColor: 255,
                fontStyle: 'bold',
                fontSize: 9
            },
            alternateRowStyles: {
                fillColor: [249, 250, 251] // Gris très clair
            }
        });

        // Ajouter des totaux en bas du tableau
        const totalsRow = [
            'TOTAUX',
            '',
            globalSummary.totalMembers,
            globalSummary.totalMen,
            globalSummary.totalWomen,
            globalSummary.totalBaptized,
            globalSummary.totalNonBaptized,
            ''
        ];

        doc.autoTable({
            startY: doc.lastAutoTable.finalY,
            body: [totalsRow],
            theme: 'grid',
            styles: {
                fontSize: 8,
                cellPadding: 2,
                fontStyle: 'bold',
                fillColor: [229, 231, 235] // Gris clair
            },
            columnStyles: {
                0: { cellWidth: 60, fontStyle: 'bold' },
                1: { cellWidth: 50 },
                2: { cellWidth: 20, halign: 'center' },
                3: { cellWidth: 15, halign: 'center' },
                4: { cellWidth: 15, halign: 'center' },
                5: { cellWidth: 20, halign: 'center' },
                6: { cellWidth: 20, halign: 'center' },
                7: { cellWidth: 50 }
            }
        });

        // Pied de page avec informations supplémentaires
        const pageHeight = doc.internal.pageSize.height;
        doc.setFontSize(8);
        doc.setFont('helvetica', 'italic');
        doc.text('Ce rapport présente un aperçu complet de la répartition des membres dans le réseau JEE/SIM.', 14, pageHeight - 10);
        doc.text('H = Hommes, F = Femmes, Bapt. = Baptisés, N.Bapt. = Non-baptisés', 14, pageHeight - 5);

        // Numéro de page
        doc.setFont('helvetica', 'normal');
        doc.text(`Page 1`, doc.internal.pageSize.width - 30, pageHeight - 10);

        // Générer le fichier PDF
        const pdfBlob = doc.output('blob');
        const fileName = `recapitulatif_eglises_${new Date().toISOString().split('T')[0]}.pdf`;
        downloadFile(pdfBlob, fileName);

        return { success: true };
    } catch (error) {
        console.error("Erreur lors de l'exportation du récapitulatif:", error);
        return { success: false, error: error.message };
    }
};

// Exporter le récapitulatif des églises en Excel
export const exportChurchesSummaryToExcel = async (churchesData, globalSummary) => {
    try {
        if (!churchesData || churchesData.length === 0) {
            throw new Error("Aucune donnée d'église à exporter");
        }

        // Créer un nouveau classeur
        const workbook = XLSX.utils.book_new();

        // Préparer les données du résumé global
        const summaryData = [
            ['RÉSUMÉ GLOBAL', ''],
            ['Indicateur', 'Valeur'],
            ['Nombre total d\'églises', globalSummary.totalChurches],
            ['Total des membres', globalSummary.totalMembers],
            ['Hommes', globalSummary.totalMen],
            ['Femmes', globalSummary.totalWomen],
            ['Baptisés', globalSummary.totalBaptized],
            ['Non-baptisés', globalSummary.totalNonBaptized],
            [''], // Ligne vide
            ['DÉTAIL PAR ÉGLISE', ''],
            ['Église', 'Localisation', 'Total Membres', 'Hommes', 'Femmes', 'Baptisés', 'Non-Baptisés', 'Contact']
        ];

        // Ajouter les données des églises
        churchesData.forEach(church => {
            summaryData.push([
                church.nom,
                church.localisation || '',
                church.totalMembres,
                church.hommes,
                church.femmes,
                church.baptises,
                church.nonBaptises,
                church.contact || ''
            ]);
        });

        // Ajouter la ligne de totaux
        summaryData.push([
            'TOTAUX',
            '',
            globalSummary.totalMembers,
            globalSummary.totalMen,
            globalSummary.totalWomen,
            globalSummary.totalBaptized,
            globalSummary.totalNonBaptized,
            ''
        ]);

        const worksheet = XLSX.utils.aoa_to_sheet(summaryData);

        // Formatter les colonnes
        const columnWidths = [
            {wpx: 150}, // Église
            {wpx: 120}, // Localisation
            {wpx: 80},  // Total Membres
            {wpx: 60},  // Hommes
            {wpx: 60},  // Femmes
            {wpx: 60},  // Baptisés
            {wpx: 80},  // Non-Baptisés
            {wpx: 120}  // Contact
        ];
        worksheet['!cols'] = columnWidths;

        XLSX.utils.book_append_sheet(workbook, worksheet, "Récapitulatif Églises");

        // Générer le fichier Excel
        const excelBuffer = XLSX.write(workbook, {bookType: 'xlsx', type: 'array'});
        const blob = new Blob([excelBuffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});

        const fileName = `recapitulatif_eglises_${new Date().toISOString().split('T')[0]}.xlsx`;
        downloadFile(blob, fileName);

        return {success: true};
    } catch (error) {
        console.error("Erreur lors de l'exportation Excel du récapitulatif:", error);
        return {success: false, error: error.message};
    }
};

// Fonction utilitaire pour récupérer les membres
const fetchMembers = async (churchId = null) => {
    try {
        let members = [];

        if (churchId) {
            // Récupérer les membres d'une église spécifique
            const churchMembersRef = ref(database, `churchMembers/${churchId}`);
            const churchMembersSnapshot = await get(churchMembersRef);

            if (churchMembersSnapshot.exists()) {
                const memberPromises = Object.keys(churchMembersSnapshot.val()).map(async (uid) => {
                    const userRef = ref(database, `users/${uid}/profile`);
                    const userSnapshot = await get(userRef);

                    if (userSnapshot.exists()) {
                        return {
                            uid,
                            ...userSnapshot.val()
                        };
                    }
                    return null;
                });

                const results = await Promise.all(memberPromises);
                members = results.filter(member => member !== null);
            }
        } else {
            // Récupérer tous les membres
            const usersRef = ref(database, 'users');
            const usersSnapshot = await get(usersRef);

            if (usersSnapshot.exists()) {
                usersSnapshot.forEach((childSnapshot) => {
                    const uid = childSnapshot.key;
                    const profile = childSnapshot.val().profile;

                    if (profile) {
                        members.push({
                            uid,
                            ...profile
                        });
                    }
                });
            }
        }

        // Filtrer et nettoyer les données pour l'exportation
        return members.map(member => ({
            nom: member.nom || '',
            prenom: member.prenom || '',
            telephone: member.telephone || '',
            email: member.email || '',
            sexe: member.sexe || '',
            baptise: member.baptise ? 'Oui' : 'Non',
            profession: member.profession || ''
        }));
    } catch (error) {
        console.error("Erreur lors de la récupération des membres:", error);
        throw error;
    }
};

// Fonction utilitaire pour télécharger un fichier
const downloadFile = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();

    // Nettoyer
    setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, 100);
};

export const ExportService = {
    exportToExcel,
    exportToPDF,
    exportStatsToPDF,
    exportChurchesSummaryToPDF,
    exportChurchesSummaryToExcel
};
